import React from 'react';
import { CalendarIcon, UserIcon, CurrencyDollarIcon, MapPinIcon, ClockIcon, PhoneIcon, EnvelopeIcon } from '@heroicons/react/24/outline';

const BookingDetail = ({ booking }) => {
  // Format date for display
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Render status badge
  const renderStatusBadge = (status) => {
    // Handle undefined or null status
    if (status === undefined || status === null) {
      return (
        <span className="px-3 py-1 inline-flex text-sm leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
          Unknown
        </span>
      );
    }
    
    let bgColor = 'bg-gray-100';
    let textColor = 'text-gray-800';
    
    switch (status.toLowerCase()) {
      case 'confirmed':
        bgColor = 'bg-green-100';
        textColor = 'text-green-800';
        break;
      case 'pending':
        bgColor = 'bg-yellow-100';
        textColor = 'text-yellow-800';
        break;
      case 'cancelled':
        bgColor = 'bg-red-100';
        textColor = 'text-red-800';
        break;
      case 'completed':
        bgColor = 'bg-blue-100';
        textColor = 'text-blue-800';
        break;
      default:
        break;
    }
    
    return (
      <span className={`px-3 py-1 inline-flex text-sm leading-5 font-semibold rounded-full ${bgColor} ${textColor}`}>
        {status}
      </span>
    );
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-start mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Booking Details</h2>
        {renderStatusBadge(booking.status)}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Booking Information */}
        <div className="space-y-6">
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-3">Booking Information</h3>
            <div className="bg-gray-50 rounded-lg p-4 space-y-3">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <span className="flex items-center justify-center h-10 w-10 rounded-md bg-primary-light text-white">
                    <CalendarIcon className="h-6 w-6" aria-hidden="true" />
                  </span>
                </div>
                <div className="ml-4">
                  <dt className="text-sm font-medium text-gray-500">Booking Number</dt>
                  <dd className="mt-1 text-sm text-gray-900">{booking.booking_number}</dd>
                </div>
              </div>
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <span className="flex items-center justify-center h-10 w-10 rounded-md bg-primary-light text-white">
                    <CalendarIcon className="h-6 w-6" aria-hidden="true" />
                  </span>
                </div>
                <div className="ml-4">
                  <dt className="text-sm font-medium text-gray-500">Tour Date</dt>
                  <dd className="mt-1 text-sm text-gray-900">{formatDate(booking.date)}</dd>
                </div>
              </div>
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <span className="flex items-center justify-center h-10 w-10 rounded-md bg-primary-light text-white">
                    <UserIcon className="h-6 w-6" aria-hidden="true" />
                  </span>
                </div>
                <div className="ml-4">
                  <dt className="text-sm font-medium text-gray-500">Number of Travelers</dt>
                  <dd className="mt-1 text-sm text-gray-900">{booking.travelers}</dd>
                </div>
              </div>
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <span className="flex items-center justify-center h-10 w-10 rounded-md bg-primary-light text-white">
                    <CurrencyDollarIcon className="h-6 w-6" aria-hidden="true" />
                  </span>
                </div>
                <div className="ml-4">
                  <dt className="text-sm font-medium text-gray-500">Total Amount</dt>
                  <dd className="mt-1 text-sm text-gray-900">${booking.total_amount.toLocaleString()}</dd>
                </div>
              </div>
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <span className="flex items-center justify-center h-10 w-10 rounded-md bg-primary-light text-white">
                    <ClockIcon className="h-6 w-6" aria-hidden="true" />
                  </span>
                </div>
                <div className="ml-4">
                  <dt className="text-sm font-medium text-gray-500">Created At</dt>
                  <dd className="mt-1 text-sm text-gray-900">{formatDate(booking.created_at)}</dd>
                </div>
              </div>
            </div>
          </div>

          {/* Tour Information */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-3">Tour Information</h3>
            <div className="bg-gray-50 rounded-lg p-4 space-y-3">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <span className="flex items-center justify-center h-10 w-10 rounded-md bg-primary-light text-white">
                    <MapPinIcon className="h-6 w-6" aria-hidden="true" />
                  </span>
                </div>
                <div className="ml-4">
                  <dt className="text-sm font-medium text-gray-500">Tour Name</dt>
                  <dd className="mt-1 text-sm text-gray-900">{booking.tour.name}</dd>
                </div>
              </div>
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <span className="flex items-center justify-center h-10 w-10 rounded-md bg-primary-light text-white">
                    <MapPinIcon className="h-6 w-6" aria-hidden="true" />
                  </span>
                </div>
                <div className="ml-4">
                  <dt className="text-sm font-medium text-gray-500">Location</dt>
                  <dd className="mt-1 text-sm text-gray-900">{booking.tour.location}</dd>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Customer Information */}
        <div className="space-y-6">
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-3">Customer Information</h3>
            <div className="bg-gray-50 rounded-lg p-4 space-y-3">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <span className="flex items-center justify-center h-10 w-10 rounded-md bg-primary-light text-white">
                    <UserIcon className="h-6 w-6" aria-hidden="true" />
                  </span>
                </div>
                <div className="ml-4">
                  <dt className="text-sm font-medium text-gray-500">Customer Name</dt>
                  <dd className="mt-1 text-sm text-gray-900">{booking.customer.name}</dd>
                </div>
              </div>
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <span className="flex items-center justify-center h-10 w-10 rounded-md bg-primary-light text-white">
                    <EnvelopeIcon className="h-6 w-6" aria-hidden="true" />
                  </span>
                </div>
                <div className="ml-4">
                  <dt className="text-sm font-medium text-gray-500">Email</dt>
                  <dd className="mt-1 text-sm text-gray-900">{booking.customer.email}</dd>
                </div>
              </div>
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <span className="flex items-center justify-center h-10 w-10 rounded-md bg-primary-light text-white">
                    <PhoneIcon className="h-6 w-6" aria-hidden="true" />
                  </span>
                </div>
                <div className="ml-4">
                  <dt className="text-sm font-medium text-gray-500">Phone</dt>
                  <dd className="mt-1 text-sm text-gray-900">{booking.customer.phone}</dd>
                </div>
              </div>
            </div>
          </div>

          {/* Payment Information */}
          {booking.payment && (
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-3">Payment Information</h3>
              <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <span className="flex items-center justify-center h-10 w-10 rounded-md bg-primary-light text-white">
                      <CurrencyDollarIcon className="h-6 w-6" aria-hidden="true" />
                    </span>
                  </div>
                  <div className="ml-4">
                    <dt className="text-sm font-medium text-gray-500">Payment Method</dt>
                    <dd className="mt-1 text-sm text-gray-900">{booking.payment.method || 'Not specified'}</dd>
                  </div>
                </div>
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <span className="flex items-center justify-center h-10 w-10 rounded-md bg-primary-light text-white">
                      <CurrencyDollarIcon className="h-6 w-6" aria-hidden="true" />
                    </span>
                  </div>
                  <div className="ml-4">
                    <dt className="text-sm font-medium text-gray-500">Payment Status</dt>
                    <dd className="mt-1 text-sm text-gray-900">{booking.payment.status || 'Not specified'}</dd>
                  </div>
                </div>
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <span className="flex items-center justify-center h-10 w-10 rounded-md bg-primary-light text-white">
                      <CalendarIcon className="h-6 w-6" aria-hidden="true" />
                    </span>
                  </div>
                  <div className="ml-4">
                    <dt className="text-sm font-medium text-gray-500">Payment Date</dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {booking.payment.date ? formatDate(booking.payment.date) : 'Not specified'}
                    </dd>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Notes */}
          {booking.notes && (
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-3">Notes</h3>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-900">{booking.notes}</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Booking History */}
      {booking.history && booking.history.length > 0 && (
        <div className="mt-8">
          <h3 className="text-lg font-medium text-gray-900 mb-3">Booking History</h3>
          <div className="bg-gray-50 rounded-lg p-4">
            <ul className="space-y-4">
              {booking.history.map((item, index) => (
                <li key={index} className="border-b border-gray-200 pb-4 last:border-b-0 last:pb-0">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-900">{item.action}</p>
                      <p className="text-sm text-gray-500">{item.notes}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-gray-500">{formatDate(item.date)}</p>
                      <p className="text-sm text-gray-500">{item.user}</p>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

export default BookingDetail;
